import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { FormProvider, useForm, useWatch } from "react-hook-form";

import { Stack } from "@mui/material";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import makeStyles from "@mui/styles/makeStyles";

import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { SelectButton } from "primereact/selectbutton";

import { UTP2CustomQuestionTypes } from "../../../shared/enums/UTP2";
import { CustomQuestionField } from "../../../shared/interfaces/Workflow";
import { ValidationMessage } from "../../../shared/ValidationMessage";

import { CancelButton } from "../../../shared/CancelButton";
import { DropdownBuilder } from "./DropdownBuilder";
import { RegexMatchTester } from "./RegexMatchTester";
import { TextValidationBuilder } from "./TextValidationBuilder";

interface Props {
  customQuestionField?: CustomQuestionField;
  open?: boolean;
  onConfirm: (questionField: CustomQuestionField) => void;
  onCancel: () => void;
}

const useStyles = makeStyles((theme) => ({
  button: {
    padding: theme.spacing(1),
    marginLeft: theme.spacing(1),
  },
  gridContainer: {
    margin: theme.spacing(1, 0),
    paddingRight: theme.spacing(2),
  },
  gridItem: {
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    flexDirection: "column",
  },
  inputText: {
    width: "100%",
  },
  title: {
    fontSize: 14,
    width: "100%",

    display: "flex",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    marginRight: theme.spacing(1),
  },
  titleContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-end",
  },

  testerDropdown: {
    width: "200px",
  },
}));

interface FormOption {
  value: string;
  index: number;
  array: string[];
}

const emptyField: CustomQuestionField = {
  name: "",
  type: UTP2CustomQuestionTypes.TextInput,
  value: "",
  readonly: false,
  mandatory: null,
  validation: "",
  validation_message: null,
  options: null,
};

export function CustomQuestionBuilderDialog(props: Props) {
  const { t } = useTranslation();
  const classes = useStyles();

  const formValidation = useForm({
    reValidateMode: "onChange",
    defaultValues: props.customQuestionField
      ? {
          ...props.customQuestionField,
          formOptions: props.customQuestionField?.options?.map((o) => ({ value: o }) as FormOption),
        }
      : { ...emptyField, formOptions: [] },
  });

  const {
    control,
    register,
    unregister,
    formState: { errors },
    handleSubmit,
    getValues,
  } = formValidation;

  const [formOptions, validation] = useWatch({
    control,
    name: ["formOptions", "validation"],
  });

  const [responseType, setResponseType] = useState<UTP2CustomQuestionTypes>(UTP2CustomQuestionTypes.TextInput);
  const [responseListIncludeOther, setResponseListIncludeOther] = useState(false);

  const [testerDropdownFieldValue, setTesterDropdownFieldValue] = useState("");

  useEffect(() => {
    if (responseType === UTP2CustomQuestionTypes.Dropdown)
      register("formOptions", {
        validate: {
          listHasOptions: (value) => Array.isArray(value) && value.length > 0,
        },
      });
    else unregister("formOptions");

    if (responseType !== UTP2CustomQuestionTypes.TextInput) unregister("validation");
  }, [responseType, register, unregister]);

  useEffect(() => {
    if (props.customQuestionField) {
      if (props.customQuestionField.type === UTP2CustomQuestionTypes.ExclusiveDropdown) {
        setResponseType(UTP2CustomQuestionTypes.Dropdown);
        setResponseListIncludeOther(false);
      } else if (props.customQuestionField.type === UTP2CustomQuestionTypes.Dropdown) {
        setResponseType(UTP2CustomQuestionTypes.Dropdown);
        setResponseListIncludeOther(true);
      } else {
        setResponseType(props.customQuestionField.type);
      }
    }
  }, [props.customQuestionField]);

  const responseTypeOptions = [
    {
      name: t("customQuestions.text"),
      value: UTP2CustomQuestionTypes.TextInput,
    },
    { name: t("common.date"), value: UTP2CustomQuestionTypes.Date },
    {
      name: t("customQuestions.dropdown"),
      value: UTP2CustomQuestionTypes.Dropdown,
    },
  ];

  const handleConfirmButtonClick = handleSubmit(() => {
    const formValues = getValues();

    const { formOptions, ...updatedQuestion } = formValues;
    updatedQuestion.type = responseType;

    if (responseType === UTP2CustomQuestionTypes.Dropdown) {
      updatedQuestion.type = responseListIncludeOther
        ? UTP2CustomQuestionTypes.Dropdown
        : UTP2CustomQuestionTypes.ExclusiveDropdown;
      updatedQuestion.options = formValues.formOptions?.map((formOption) => formOption.value) || null;
    }

    props.onConfirm(updatedQuestion);
  });

  const header = <span>{t("customQuestions.customQuestionBuilder")}</span>;

  const footer = (
    <Stack direction="row" justifyContent="flex-end" spacing={1} marginTop={0}>
      <Button
        type="button"
        variant="contained"
        onClick={handleConfirmButtonClick}
        color="primary"
        className={classes.button}
      >
        {props.customQuestionField ? t("common.update") : t("common.add")}{" "}
      </Button>
      <CancelButton onClick={props.onCancel} className={classes.button} />
    </Stack>
  );

  const onSelectResponseType = (responseType: UTP2CustomQuestionTypes) => {
    if (responseType != null) {
      setResponseType(responseType);
    }
  };

  return (
    <Dialog
      header={header}
      visible={props.open}
      onHide={props.onCancel}
      footer={footer}
      breakpoints={{ "600px": "90vw" }}
      style={{ width: "60vw", paddingBottom: "0px" }}
    >
      <FormProvider {...formValidation}>
        <Grid container marginY={2}>
          <Grid item xs={3} display="flex" alignItems="center">
            <Typography className={classes.title} color="textPrimary" variant="subtitle2">
              {t("customQuestions.question")}
            </Typography>
          </Grid>

          <Grid item xs>
            <InputText
              placeholder={t("customQuestions.questionPlaceholder")}
              className={classes.inputText}
              {...register("name", { required: true })}
            />
            {errors.name && (
              <ValidationMessage
                invalidMessage={t("customQuestions.questionRequired")}
                validationType="error"
                showIcon
              />
            )}
          </Grid>
        </Grid>

        <Divider />

        <Grid container marginY={2}>
          <Grid item xs={3} display="flex" alignItems="center">
            <Typography className={classes.title} color="textPrimary" variant="subtitle2">
              {t("customQuestions.responseFormat")}
            </Typography>
          </Grid>

          <Grid item>
            <SelectButton
              value={responseType}
              optionLabel="name"
              options={responseTypeOptions}
              onChange={(e) => onSelectResponseType(e.value)}
            />
          </Grid>
        </Grid>

        <Divider />

        {(responseType === UTP2CustomQuestionTypes.TextInput || responseType === UTP2CustomQuestionTypes.Dropdown) && (
          <>
            <Grid container marginY={2}>
              <Grid item>
                {responseType === UTP2CustomQuestionTypes.TextInput && (
                  <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={1}>
                    <Typography className={classes.title} color="textPrimary" variant="subtitle2">
                      {t("customQuestions.textValidation")}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {t("common.optional")}
                    </Typography>
                  </Stack>
                )}

                {responseType === UTP2CustomQuestionTypes.Dropdown && (
                  <Typography className={classes.title} color="textPrimary" variant="subtitle2" gutterBottom>
                    {t("customQuestions.options")}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12} className={classes.gridItem}>
                {responseType === UTP2CustomQuestionTypes.TextInput && <TextValidationBuilder />}

                {responseType === UTP2CustomQuestionTypes.Dropdown && (
                  <DropdownBuilder
                    includeOther={responseListIncludeOther}
                    onIncludeOtherCheckboxChange={(checked) => setResponseListIncludeOther(checked)}
                  />
                )}
              </Grid>
            </Grid>
            <Divider />

            {(responseType === UTP2CustomQuestionTypes.Dropdown ||
              (responseType === UTP2CustomQuestionTypes.TextInput && validation)) && (
              <>
                <Grid container marginY={2}>
                  <Grid item xs={3} display="flex" alignItems="center">
                    <Typography className={classes.title} color="textPrimary" variant="subtitle2">
                      {t("customQuestions.tryItOut")}
                    </Typography>
                  </Grid>
                  <Grid item xs className={classes.gridItem}>
                    {responseType === UTP2CustomQuestionTypes.TextInput && validation && (
                      <RegexMatchTester regex={validation} />
                    )}

                    {responseType === UTP2CustomQuestionTypes.Dropdown && (
                      <Dropdown
                        value={testerDropdownFieldValue}
                        options={[
                          ...(formOptions?.filter((o) => o.value !== "").map((o) => o.value) || []),
                          ...(responseListIncludeOther ? [t("customQuestions.other")] : []),
                        ]}
                        onChange={(e: any) => setTesterDropdownFieldValue(e.value)}
                        className={classes.testerDropdown}
                        emptyMessage={t("customQuestions.dropdownEmpty")}
                      />
                    )}
                  </Grid>
                </Grid>
                <Divider />
              </>
            )}
          </>
        )}
      </FormProvider>
    </Dialog>
  );
}
